import {
  useState, ReactNode, useEffect, useRef, useLayoutEffect,
} from 'react';
import styled from 'styled-components';
import { Input, InputCss, InputFocus } from './Input';
import { useFormConfigurationContext } from '../../../..';

const TextareaFieldInput = styled.textarea<{ styles: string, hasError: boolean, disabled?: boolean, minHeight?: number, maxHeight?: number }>`
${InputCss};
${({
    theme, disabled, hasError, minHeight = 40, maxHeight = 132,
  }) => `
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  padding:${theme.padding[2]};
  padding-left:${theme.padding[4]};
  padding-right:${theme.padding[4]};
  resize: none;
  background-color: ${theme.colours.neutral[10]};
  min-height: ${minHeight}px;
  max-height: ${maxHeight}px;
  border-color: ${hasError ? theme.textDanger : theme.colours.neutral[6]};
  ${!disabled && `
    &:hover {
      background-color: ${theme.colours.neutral[10]};
      border-color: ${hasError ? theme.textDanger : theme.colours.primary[5]};
    }
  `};
  &:disabled {
    background-color: ${theme.colours.neutral[8]};
    border-color: ${theme.colours.neutral[8]};
  }
  &::placeholder {
    color:  ${theme.colours.neutral[4]};
    font-size: ${theme.fontSize[2]};
    font-weight: ${theme.fontWeight.normal}
  }
`}
${InputFocus}
`;

type TextareaFieldProps = {
  id: string,
  label?: ReactNode,
  placeholder?: string,
  value: string,
  onChange: (value: string) => void,
  labelStyles?: string,
  styles?: string,
  containerStyles?: string,
  rows?: number,
  cols?: number,
  maxLength?: number,
  minHeight?: number,
  maxHeight?: number,
  visible?: boolean,
  disabled?: boolean,
  touchOverride?: boolean,
  onFocus?: () => void,
  onBlur?: (targetValue: string) => void,
  required?: boolean,
  errors?: string[],
  warnings?: string[],
};

export function TextareaInput({
  id,
  label = '',
  placeholder = '',
  value = '',
  onChange,
  labelStyles = '',
  styles = '',
  containerStyles = '',
  rows = 5,
  cols = 5,
  maxLength,
  minHeight = 40,
  maxHeight = 132,
  visible = true,
  disabled = false,
  touchOverride = false,
  onFocus = () => { },
  onBlur = () => { },
  required = false,
  errors = [],
  warnings = [],
}: TextareaFieldProps) {
  const [touched, setTouched] = useState(false);
  const hasError = (touched || touchOverride) && !!errors?.length;

  const { isDisabled } = useFormConfigurationContext(disabled);

  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (ref && ref?.current) {
      ref.current.style.height = 'inherit';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  }, [ref?.current?.scrollHeight]);

  useLayoutEffect(() => {
    if (ref && ref?.current) {
      const inputHeight = ref.current.scrollHeight;
      if (inputHeight <= maxHeight) {
        ref.current.style.overflow = 'hidden';
      } else {
        ref.current.style.overflow = 'auto';
      }
    }
  }, [ref?.current?.scrollHeight]);

  const onInputChange = (targetValue: string) => {
    onChange(targetValue);
  };

  const onTouched = (targetValue: string) => {
    setTouched(true);
    onBlur(targetValue);
  };

  useEffect(() => setTouched(false), [visible]);

  return (
    <>
      {visible && (
        <Input
          id={id}
          label={label}
          errors={errors}
          warnings={warnings}
          hasError={hasError}
          labelStyles={labelStyles}
          containerStyles={containerStyles}
          required={required}
        >
          <TextareaFieldInput
            id={`${id}-textarea-input`}
            ref={ref}
            value={value}
            onChange={(e) => onInputChange(e.target.value)}
            styles={styles}
            placeholder={placeholder}
            hasError={hasError}
            disabled={isDisabled}
            onFocus={onFocus}
            onBlur={(e) => onTouched(e.target.value)}
            cols={cols}
            rows={rows}
            minHeight={minHeight}
            maxHeight={maxHeight}
            maxLength={maxLength}
            autoComplete="off"
          />
        </Input>
      )}
    </>
  );
}
